import axios from "axios";

import {
  getApiBaseUrl,
  PORTAL_BASE_URL,
  PORTAL_CONFIG_API_URL,
  withTrailingSlash,
} from "../config/urls";
import { resetOnboardingForNewSession } from "../onboarding";

// Login paths keyed by the UI dropdown value.
const FALLBACK_LOGIN_PATHS = {
  ergo: "/client_login",
  bajaj: "/login",
  hegic: "/login",
};

// ---------------------------------------------------------------------------
// Portal access token
// This is the token returned by portal-configuration-api.dev.camcom.ai/api/login.
// It is the single auth token for all portal configuration API calls.
// ---------------------------------------------------------------------------

export const setPortalAccessToken = (token) => {
  if (token) {
    localStorage.setItem("portalAccessToken", token);
    console.log("[Auth] Portal access_token stored");
  } else {
    localStorage.removeItem("portalAccessToken");
  }
};

export const getPortalAccessToken = () =>
  localStorage.getItem("portalAccessToken");

// ---------------------------------------------------------------------------
// Client API token (used by apiService for cases/assessment calls)
// ---------------------------------------------------------------------------

export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem("authToken", token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    localStorage.removeItem("authToken");
    delete axios.defaults.headers.common["Authorization"];
  }
};

export const getAuthToken = () => localStorage.getItem("authToken");

export const isAuthenticated = () => !!getAuthToken();

// ---------------------------------------------------------------------------
// Source name — always the UI dropdown value ("bajaj" / "ergo").
// Stored in two keys for forward-compatibility; both hold the same value.
// ---------------------------------------------------------------------------

export const setUiSourceName = (name) => {
  if (name) {
    localStorage.setItem("ui_source_name", name);
  } else {
    localStorage.removeItem("ui_source_name");
  }
};

export const getUiSourceName = () => localStorage.getItem("ui_source_name");

export const setSourceName = (sourceName) => {
  if (sourceName) {
    localStorage.setItem("source_name", sourceName);
    console.log("[Auth] source_name set:", sourceName);
  } else {
    localStorage.removeItem("source_name");
  }
};

export const getSourceName = () => localStorage.getItem("source_name");

// ---------------------------------------------------------------------------
// Logout – clears every piece of session state
// ---------------------------------------------------------------------------

export const logout = () => {
  setAuthToken(null);
  setPortalAccessToken(null);
  ["userProfile", "source_name", "ui_source_name", "portalConfig", "permissions"].forEach(
    (k) => localStorage.removeItem(k),
  );
  // Cached case rows are somebody's work queue. They must not survive a sign
  // out, or the next person at this browser sees the last one's cases.
  try {
    Object.keys(window.sessionStorage)
      .filter((k) => k.startsWith("list:") || k.startsWith("scroll:"))
      .forEach((k) => window.sessionStorage.removeItem(k));
  } catch {
    /* storage disabled */
  }
  console.log("[Auth] User logged out – all session state cleared");
};

// ---------------------------------------------------------------------------
// Login — two-step flow
//
// Step 1 – Portal login (portal-configuration-api.dev.camcom.ai/api/login)
//   Body: JSON { source_name, email, password }
//   On success  → stores portalAccessToken (used by portalApiClient for all
//                 portal config calls)
//   On 401/cred → returns the error immediately; no point trying client API
//   On network  → warns and skips; portal config won't work but client login
//                 can still succeed
//
// Step 2 – Client API login (bagicpp / ergo /login endpoint)
//   Body: form-encoded { email, password }
//   On success → stores authToken + user profile + source_name
//   Returns the full response payload to the caller
//
// `uiSourceName` – dropdown value ("ergo" / "bajaj")
// `loginUrl`     – optional override URL from /api/login-config (Phase 2)
// ---------------------------------------------------------------------------

export const loginUser = async (email, password, uiSourceName, loginUrl) => {
  // ── Portal login ────────────────────────────────────────────────────────
  // The portal token is valid for ALL APIs (cases, assessment, portal-config).
  // On success we store it as both authToken and portalAccessToken and return
  // immediately — no separate client-API login step needed.
  // On credential rejection we return the error.
  // On network/unavailable we fall through to the client-API fallback.
  try {
    console.log("[Auth] Portal login → source:", uiSourceName);
    const portalRes = await axios.post(
      `${PORTAL_CONFIG_API_URL}/api/login`,
      { source_name: uiSourceName, email, password },
      { headers: { "Content-Type": "application/json", Accept: "*/*" } },
    );
    // Response: { status: true, message: "Success", data: { token, email, ... } }
    const pd = portalRes.data;
    const token =
      pd?.data?.token ||
      pd?.data?.access_token ||
      pd?.access_token ||
      (pd?.status === 1 ? pd?.token : null);

    if (token) {
      console.log("[Auth] Portal login succeeded");
      const userData = pd.data || pd;
      _storeSession(token, uiSourceName, userData);
      setPortalAccessToken(token);
      // Spread FIRST so the normalised status/token always win. With the spread
      // last, a status field inside data would overwrite the success marker and
      // Login.js would read a successful login as a failure.
      return { ...userData, status: 1, token };
    }

    // The portal wraps the client's reply: the OUTER status is true/"Success"
    // for any answered request, and the real outcome sits in data.status. Only
    // checking the outer status meant a plain wrong password was classed as an
    // "unexpected shape", triggering a pointless second request to the client
    // API and a misleading console warning.
    const innerStatus = pd?.data?.status;
    const rejected =
      pd?.status === false ||
      pd?.status === -1 ||
      pd?.status === 0 ||
      innerStatus === -1 ||
      innerStatus === "-1";

    if (rejected) {
      const why = pd?.data?.message || pd?.data?.msg || pd?.data?.Error || pd?.message;
      console.log("[Auth] Portal login rejected:", why || "(no reason given)");
      // Normalise to the flat shape Login.js expects, so it shows the credential
      // error instead of a generic failure.
      return { status: -1, message: why, ...(pd?.data || {}) };
    }

    console.warn(
      "[Auth] Portal login returned an unrecognised shape, trying client fallback:",
      JSON.stringify(pd)?.slice(0, 200),
    );
  } catch (portalErr) {
    console.warn(
      "[Auth] Portal login unavailable (",
      portalErr.response?.status ?? portalErr.message,
      ") — trying client login",
    );
  }

  // ── Client-API fallback (only reached if portal login was unavailable) ──
  const path = FALLBACK_LOGIN_PATHS[uiSourceName] || "/client_login";
  const clientUrl = loginUrl || `${getApiBaseUrl(uiSourceName)}${path}`;
  console.log("[Auth] Client login →", clientUrl);

  try {
    const clientRes = await axios.post(
      clientUrl,
      new URLSearchParams({ email, password }),
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
          Referer: withTrailingSlash(PORTAL_BASE_URL),
          "User-Agent":
            "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/140.0.0.0 Safari/537.36",
          Accept: "*/*",
        },
      },
    );
    const data = clientRes.data;
    const clientToken =
      data?.access_token || (data?.status === 1 ? data?.token : null);
    if (clientToken) {
      console.log("[Auth] Client login succeeded");
      _storeSession(clientToken, uiSourceName, data);
    }
    return data;
  } catch (err) {
    // The client-API login is the last thing tried, so its failure is what the
    // user sees. Say which host and route produced it: a 404 here means this
    // client's login endpoint is not on the host it was routed to.
    const status = err.response?.status;
    console.error(
      `[Auth] Client login failed: POST ${clientUrl} →`,
      status ?? err.message,
      status === 404
        ? "— wrong host for this client, or a dev-proxy path this server was " +
          "not started with (setupProxy.js is read once, at startup)."
        : "",
    );
    if (err.response) return err.response.data;
    throw err;
  }
};

// Store client API session state (authToken + profile).
// portalAccessToken is set separately in Step 1 of loginUser.
function _storeSession(authTok, uiSource, data) {
  setAuthToken(authTok);

  // ui_source_name: the short portal-facing name ("bajaj" / "ergo")
  // Use the UI dropdown value ("bajaj" / "ergo") as source_name everywhere.
  // Never use data.client_source_name — that is a production backend identifier.
  setUiSourceName(uiSource);
  setSourceName(uiSource);

  const userProfile = {
    email: data.email,
    access_level: data.access_level,
    client_source_name: uiSource,
    client_type: data.client_type,
    country_id: data.country_id,
    motor_flag: data.motor_flag,
    property_flag: data.property_flag,
  };
  localStorage.setItem("userProfile", JSON.stringify(userProfile));
  console.log("[Auth] Session stored | source:", uiSource);

  // Demo mode: replay onboarding nudges on every login.
  resetOnboardingForNewSession();
}

// ---------------------------------------------------------------------------
// User permissions
//
// Hegic-generation backends expose GET /get_user_permissions?email_id=<email>,
// returning { email_id, permission: "1,3,2" } — a comma-separated code list that
// the old portal uses to decide which post-login modules a user may see.
// Absent on the Bajaj/Ergo generation, where it 404s; callers treat failure as
// "no permission data" rather than an error.
// ---------------------------------------------------------------------------

export const setUserPermissions = (permissions) => {
  if (permissions) {
    localStorage.setItem("permissions", permissions);
  } else {
    localStorage.removeItem("permissions");
  }
};

export const getUserPermissions = () => localStorage.getItem("permissions");

export const fetchUserPermissions = async (email, uiSourceName) => {
  const token = getAuthToken();
  if (!token || !email) return null;

  try {
    const res = await axios.get(
      `${getApiBaseUrl(uiSourceName)}/get_user_permissions`,
      {
        params: { email_id: email },
        headers: { Authorization: `Bearer ${token}`, Accept: "*/*" },
      },
    );
    const permissions = res.data?.permission ?? null;
    if (permissions) {
      setUserPermissions(permissions);
      console.log("[Auth] permissions:", permissions);
    }
    return permissions;
  } catch (err) {
    // 404 on clients without this endpoint; never fatal to a login.
    console.info(
      "[Auth] permissions unavailable:",
      err.response?.status ?? err.message,
    );
    return null;
  }
};

// ---------------------------------------------------------------------------
// Profile helpers
// ---------------------------------------------------------------------------

export const getUserProfile = () => {
  const p = localStorage.getItem("userProfile");
  return p ? JSON.parse(p) : null;
};

// Called in App.js on mount – restores Axios auth headers from localStorage
export const initializeAuth = () => {
  const authToken = getAuthToken();
  if (authToken) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${authToken}`;
    console.log("[Auth] Auth token restored from localStorage");
  }
  if (getPortalAccessToken()) {
    console.log("[Auth] Portal access token present in localStorage");
  }
};
